import type { ModelVersionEntry } from '@avisoma/shared';
import { formatDateTime } from '../lib/dateFormat';

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** i;
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

const rowCountOf = (version: ModelVersionEntry): number =>
  version.tables.reduce((sum, table) => sum + (table.rowCount ?? 0), 0);

const KIND_LABEL: Record<string, string> = {
  model: 'Model',
  corpus: 'Corpus',
  feedback: 'Feedback',
};

const KIND_CLASSES: Record<string, string> = {
  model: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300',
  corpus: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300',
  feedback: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300',
};

export function ModelVersionTable({ versions }: { versions: ModelVersionEntry[] }) {
  return (
    <div className="overflow-x-auto max-h-80 overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 z-10 bg-white dark:bg-zinc-800">
          <tr className="border-b border-gray-200 dark:border-zinc-700 text-left text-xs text-gray-500 dark:text-zinc-400">
            <th className="py-1.5 pr-3 font-medium">Kind</th>
            <th className="py-1.5 pr-3 font-medium">Model Hash</th>
            <th className="py-1.5 pr-3 font-medium">Source Env</th>
            <th className="py-1.5 pr-3 text-right font-medium">Rows</th>
            <th className="py-1.5 pr-3 text-right font-medium">Objects</th>
            <th className="py-1.5 pr-3 text-right font-medium">Size</th>
            <th className="py-1.5 font-medium">Created</th>
          </tr>
        </thead>
        <tbody>
          {versions.map((version) => (
            <tr key={version.id} className="border-b border-gray-100 dark:border-zinc-800">
              <td className="py-1.5 pr-3">
                <span
                  className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ${
                    KIND_CLASSES[version.kind] ?? 'bg-gray-100 text-gray-700 dark:bg-zinc-700 dark:text-zinc-300'
                  }`}
                >
                  {KIND_LABEL[version.kind] ?? version.kind}
                </span>
              </td>
              <td className="py-1.5 pr-3">
                <span
                  className="font-mono text-xs text-gray-900 dark:text-zinc-100 select-text"
                  title={`${version.modelHash} (schema v${version.schemaVersion})`}
                >
                  {version.modelHash.slice(0, 12)}
                </span>
              </td>
              <td className="py-1.5 pr-3 text-gray-500 dark:text-zinc-400">{version.sourceEnv}</td>
              <td className="py-1.5 pr-3 text-right tabular-nums text-gray-900 dark:text-zinc-100">
                {rowCountOf(version).toLocaleString()}
              </td>
              <td className="py-1.5 pr-3 text-right tabular-nums text-gray-500 dark:text-zinc-400">
                {version.objectCount.toLocaleString()}
              </td>
              <td className="py-1.5 pr-3 text-right tabular-nums text-gray-500 dark:text-zinc-400">
                {formatBytes(version.bytes)}
              </td>
              <td className="py-1.5 text-gray-500 dark:text-zinc-400">{formatDateTime(version.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
